import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Package, ShoppingCart, Truck, Users, Boxes,
  Wrench, Tags, Smartphone, HandCoins, Receipt, CalendarClock,
  UserCog, ListTodo, Megaphone, TrendingUp, Zap, FileBarChart,
  LogOut, Menu, Banknote, History, PackageSearch, ScrollText,
  Cpu, Globe, ArrowLeftRight, Building2, Coins, Calculator, Warehouse, UserCircle, ShieldCheck,
} from "lucide-react";
import logo from "@/assets/angkasa-logo.jpeg";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, priv: true },
  { section: "Transaksi" },
  { to: "/kasir", label: "Kasir / Penjualan", icon: ShoppingCart },
  { to: "/riwayat-penjualan", label: "Riwayat Penjualan", icon: History },
  { to: "/cash-drawer", label: "Cash Drawer / Shift", icon: Banknote },
  { to: "/ppob", label: "PPOB", icon: Zap },
  { to: "/pengeluaran", label: "Pengeluaran", icon: Receipt },
  { to: "/pembelian", label: "Pembelian", icon: Truck, priv: true },
  { section: "Inventaris" },
  { to: "/produk", label: "Produk", icon: Package, priv: true },
  { to: "/sparepart", label: "Sparepart Servis", icon: Cpu, priv: true },
  { to: "/stok", label: "Stok & Opname", icon: Boxes },
  { to: "/cek-stok", label: "Cek Stok Global", icon: Globe },
  { to: "/transfer", label: "Transfer / Mutasi", icon: ArrowLeftRight },
  { to: "/analisis-stok", label: "Analisis Stok", icon: PackageSearch, priv: true },
  { to: "/harga-hp", label: "Estimasi Harga HP Bekas", icon: Smartphone },
  { section: "Service" },
  { to: "/service", label: "Data Service", icon: Wrench },
  { to: "/harga-service", label: "Master Harga Service", icon: Tags },
  { to: "/cek-imei", label: "Cek IMEI Bea Cukai", icon: ShieldCheck },
  { section: "Aktivitas" },
  { to: "/absensi", label: "Absensi", icon: CalendarClock },
  { to: "/konten", label: "Jadwal Konten", icon: Megaphone },
  { section: "Relasi" },
  { to: "/pelanggan", label: "Pelanggan", icon: Users },
  { to: "/supplier", label: "Supplier", icon: Truck, priv: true },
  { to: "/hutang-piutang", label: "Hutang / Piutang", icon: HandCoins, priv: true },
  { section: "Karyawan", priv: true },
  { to: "/jobdesk", label: "Jobdesk / Task", icon: ListTodo, priv: true },
  { to: "/staf", label: "Data Karyawan", icon: UserCog, priv: true },
  { to: "/kasbon", label: "Kasbon", icon: Coins, priv: true },
  { to: "/penggajian", label: "Penggajian & Slip", icon: FileBarChart, priv: true },
  { to: "/kinerja", label: "Kinerja Karyawan", icon: TrendingUp, priv: true },
  { to: "/log-aktivitas", label: "Log Aktivitas", icon: ScrollText, priv: true },
  { section: "Keuangan & Aset", priv: true },
  { to: "/aset", label: "Aset Toko", icon: Warehouse, priv: true },
  { to: "/laporan", label: "Laporan Keuangan", icon: FileBarChart, priv: true },
  { to: "/laba-bersih", label: "Laba Rugi Bersih", icon: Calculator, priv: true },
  { to: "/cabang", label: "Kelola Cabang", icon: Building2, priv: true },
  { section: "Akun" },
  { to: "/profil", label: "Profil Saya", icon: UserCircle },
];

export default function Layout({ children }) {
  const { user, logout, isPriv } = useAuth();
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const navigate = useNavigate();
  const activeBranch = localStorage.getItem("branch_id") || "all";

  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {}); }, []);

  const changeBranch = (v) => { localStorage.setItem("branch_id", v); window.location.reload(); };
  const myBranchName = branches.find((b) => b.id === user?.branch_id)?.name || "Cabang";

  const Sidebar = (
    <aside className="w-64 bg-white text-slate-600 flex flex-col h-full shrink-0 border-r border-sky-100">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-sky-100 bg-gradient-to-r from-sky-50 to-white">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center p-0.5 shrink-0 shadow-sm ring-1 ring-sky-100 overflow-hidden">
          <img src={logo} alt="ANGKASA STORE" className="w-full h-full object-cover rounded-lg" />
        </div>
        <div>
          <div className="text-sky-700 font-bold tracking-wide leading-tight">ANGKASA STORE</div>
          <div className="text-[11px] text-sky-400">Manajemen Toko HP</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {NAV.map((item, i) => {
          if (item.section) {
            if (item.priv && !isPriv) return null;
            return (
              <div key={i} className="px-2 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sky-400">
                {item.section}
              </div>
            );
          }
          if (item.priv && !isPriv) return null;
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.to.replace(/\//g, "") || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-sky-600 text-white font-medium shadow-sm shadow-sky-200" : "hover:bg-sky-50 hover:text-sky-700"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );

  return (
    <div className="flex h-screen bg-sky-50/40 overflow-hidden">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-sky-100 flex items-center justify-between px-4 md:px-6 sticky top-0 z-40 gap-3">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          {/* Branch selector */}
          {user?.role === "owner" ? (
            <select
              value={activeBranch}
              onChange={(e) => changeBranch(e.target.value)}
              data-testid="branch-selector"
              className="text-sm border border-sky-200 rounded-lg px-3 py-1.5 bg-sky-50 text-sky-700 font-medium focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">🏬 Semua Cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          ) : (
            <span className="text-sm border border-sky-200 rounded-lg px-3 py-1.5 bg-sky-50 text-sky-700 font-medium flex items-center gap-1.5" data-testid="branch-fixed">
              <Building2 className="w-4 h-4" /> {myBranchName}
            </span>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <div className="text-right">
              <div className="text-sm font-medium text-slate-800">{user?.name}</div>
              <div className="text-[11px] uppercase text-sky-600 font-semibold">{user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="p-2 rounded-md hover:bg-sky-50 text-slate-500 hover:text-sky-700"
              title="Keluar"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
